import { Link } from '@inertiajs/react';
import type { StateResponse } from '@/types/market';
import { formatPercent } from './format';
import { useMarketState } from './live';
import { RollingNumber } from './rolling-number';

export function MarketList({
  initialData,
  hackSlug,
}: {
  initialData: StateResponse;
  hackSlug: string;
}) {
  const state = useMarketState(initialData);
  const resolved = state.market.status === 'resolved';
  const winnerId = state.market.winnerProjectId;
  const presenting = state.projects.filter((p) => p.isCurrentlyPresenting);
  const sorted = [...state.projects].sort((a, b) => {
    if (resolved) {
      if (a.id === winnerId) return -1;
      if (b.id === winnerId) return 1;
    }
    return b.price - a.price;
  });

  return (
    <div className="flex flex-col gap-6">
      {presenting.length > 0 && (
        <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
          <div className="mb-4 flex items-center justify-between border-primary/20 border-b pb-4">
            <h2 className="font-bold text-2xl text-primary">En el escenario</h2>
            <span className="inline-flex items-center gap-2 bg-primary px-3 py-1 font-mono text-background text-xs uppercase tracking-wider">
              <span className="size-2 animate-pulse bg-background" />
              Live
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {presenting.map((p) => (
              <Link
                key={p.id}
                href={`/${hackSlug}/market/${p.slug}`}
                className="group block border-2 border-primary bg-background p-4 transition-colors hover:bg-primary hover:text-background"
              >
                <div className="flex items-start gap-3">
                  {p.logoUrl ? (
                    <img
                      src={p.logoUrl}
                      alt={p.name}
                      className="size-12 border-2 border-primary object-cover group-hover:border-background"
                    />
                  ) : (
                    <div className="size-12 border-2 border-primary bg-muted group-hover:border-background" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-lg">{p.name}</p>
                    <p className="truncate font-mono text-xs opacity-70">
                      {p.teamSlug}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold font-mono text-2xl">
                      <RollingNumber value={p.price} format={formatPercent} />
                    </p>
                    <p className="font-mono text-[10px] uppercase opacity-70">
                      chance
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="mb-4 flex items-center justify-between border-primary/20 border-b pb-4">
          <h2 className="font-bold text-2xl text-primary">
            Todos los proyectos
          </h2>
          <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
            {resolved ? 'Resultado final' : 'Ordenados por probabilidad'}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {sorted.map((p, i) => {
            const isWinner = resolved && p.id === winnerId;
            return (
              <Link
                key={p.id}
                href={`/${hackSlug}/market/${p.slug}`}
                className={`group flex items-center gap-4 border-l-4 py-3 pr-3 pl-4 transition-colors hover:bg-primary/5 ${
                  isWinner
                    ? 'border-primary bg-primary/10'
                    : p.isCurrentlyPresenting
                      ? 'border-primary bg-primary/5'
                      : 'border-primary/40'
                } ${resolved && !isWinner ? 'opacity-60' : ''}`}
              >
                <span className="w-6 font-mono text-primary/60 text-xs tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {p.logoUrl ? (
                  <img
                    src={p.logoUrl}
                    alt={p.name}
                    className="size-10 border-2 border-primary object-cover"
                  />
                ) : (
                  <div className="size-10 border-2 border-primary bg-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-bold text-primary">{p.name}</p>
                    {isWinner && (
                      <span className="bg-primary px-1.5 py-0.5 font-mono text-[9px] text-background uppercase">
                        Ganador
                      </span>
                    )}
                    {!resolved && p.isCurrentlyPresenting && (
                      <span className="bg-primary px-1.5 py-0.5 font-mono text-[9px] text-background uppercase">
                        Live
                      </span>
                    )}
                  </div>
                  <p className="truncate font-mono text-primary/70 text-xs">
                    {p.teamSlug}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold font-mono text-primary text-xl tabular-nums">
                    <RollingNumber value={p.price} format={formatPercent} />
                  </p>
                  <p className="font-mono text-[10px] text-primary/60 uppercase">
                    {resolved ? 'prob. final' : 'chance'}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
